import {
	ContentBlogUnifiedQueryArtifact,
	ContentUnifiedQueryArtifact,
	ContentUnifiedWithSpaceKeyQueryArtifact,
	createContentBlogUnifiedQueryVariables,
	createContentUnifiedQueryVariables,
	createContentUnifiedWithSpaceKeyQueryVariables,
	type ContentBlogUnifiedQueryType,
	type ContentBlogUnifiedQueryVariablesType,
	type ContentUnifiedQueryType,
	type ContentUnifiedQueryVariablesType,
	type ContentUnifiedWithSpaceKeyQueryType,
	type ContentUnifiedWithSpaceKeyQueryVariablesType,
} from '@confluence/content-unified-query';
import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { isUnauthorizedError } from '@confluence/query-preloader-tools/entry-points/isUnauthorizedError.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { ssrPermissionsErrorHandler } from '@confluence/ssr-utilities/entry-points/SSRPermissionsErrorHandler.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

type PreloadContentParameters = {
	contentId: string;
	isBlog: boolean;
	spaceKey: string;
	shouldPreloadUsingSpaceKey?: boolean;
	versionOverride?: number | null;
	userId?: string | null;
	agentSessionsEnabled?: boolean;
	autohighlightsQueryVariables?: {
		entityAri: string;
		workspaceId: string;
	};
	includeRendererAssetsDsp?: boolean;
	rendererAssetsDspAction?: 'ATTACHMENT_DOWNLOAD' | 'PAGE_EXPORT';
	hasDocumentContractAnalyticsByline?: boolean;
	hasDocumentContractContentHeader?: boolean;
	includeRendererAssetsMedia?: boolean;
	/** Set by `preloadAccessLinkContent`. Not derived from the URL, so it is SSR-safe. */
	isAccessLinkRoute?: boolean;
};

type PreloadContentResult = {
	result?: any;
	hasErrors: boolean;
};

type ChooseContentUnifiedQueryVariablesType =
	| ContentUnifiedQueryVariablesType
	| ContentBlogUnifiedQueryVariablesType;

type PreloadGraphQLError = { path?: ReadonlyArray<string | number> | null };

/** A 403 on the root `space` field only nulls that field; the content still comes back. */
const isSpaceScopedError = (error: PreloadGraphQLError): boolean => error.path?.[0] === 'space';

function processContentPreloadPermissionError(result: any, isAccessLinkRoute?: boolean) {
	result?.errors?.forEach((error: any) => {
		// The "Cannot find space" condition is handled by redirecting to the correct space
		// even if error is not propagated to ContentPrerequisites where it is normally handled.
		if (isUnauthorizedError(error)) {
			// An access-link viewer has no space membership by design, so a space-scoped 403 must
			// not blank a page whose content resolved.
			if (isAccessLinkRoute && isSpaceScopedError(error)) {
				return;
			}
			ssrPermissionsErrorHandler.markSSRPreloadPermissionError('view', error);
		}
	});
}

export const transformContentData = (
	data: any,
): {
	content: {
		nodes: any[];
		// @ts-ignore
		__typename: string;
	} | null;
	space: any;
} => ({
	content: data?.spaceHomepage
		? {
				nodes: [data?.spaceHomepage],
				// @ts-ignore
				__typename: 'PaginatedContentListWithChild',
			}
		: null,
	space: data?.space || null,
});

export function preloadContent({
	contentId,
	spaceKey,
	isBlog,
	versionOverride = null,
	userId,
	agentSessionsEnabled = false,
	autohighlightsQueryVariables,
	includeRendererAssetsDsp = false,
	rendererAssetsDspAction,
	hasDocumentContractAnalyticsByline = false,
	hasDocumentContractContentHeader = false,
	includeRendererAssetsMedia = false,
	isAccessLinkRoute = false,
}: PreloadContentParameters): Promise<PreloadContentResult> {
	window.performance.mark('CFP-63.preloadContent.start');
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = Boolean(process.env.REACT_SSR);
	const shouldUseContentHeaderFragment =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		hasDocumentContractContentHeader &&
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_header_frag_reader') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false);
	const shouldUseFabricEligibilityFragment =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		shouldUseContentHeaderFragment &&
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_fabric_eligibility_frag_reader') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false);

	// Space overview
	if (spaceKey && !contentId) {
		return query<ContentUnifiedWithSpaceKeyQueryType, ContentUnifiedWithSpaceKeyQueryVariablesType>(
			{
				query: ContentUnifiedWithSpaceKeyQueryArtifact,
				variables: {
					...createContentUnifiedWithSpaceKeyQueryVariables({
						spaceKey,
						versionOverride,
						isSSR,
						includeRendererAssetsMedia,
						agentSessionsUserId: agentSessionsEnabled ? userId : undefined,
						includeAgentTrustReceiptData: fg('cc_sparks_agent_trust_receipt'),
						includeMetadata:
							!StatsigConfigurations.isKillSwitchOn('confluence_vp_meta_frag_reader') &&
							expVal('confluence_exp_vp_document', 'isEnabled', false),
						includeClassification:
							!StatsigConfigurations.isKillSwitchOn(
								'confluence_vp_classification_context_frag_reader',
							) && expVal('confluence_exp_vp_document', 'isEnabled', false),
						includeReactions:
							!StatsigConfigurations.isKillSwitchOn('confluence_vp_reactions_frag_reader') &&
							expVal('confluence_exp_vp_document', 'isEnabled', false),
						includeAudioContent:
							!StatsigConfigurations.isKillSwitchOn('confluence_vp_audio_frag_reader') &&
							expVal('confluence_exp_vp_document', 'isEnabled', false),
						autohighlightsQueryVariables,
					}),
					...(!StatsigConfigurations.isKillSwitchOn('confluence_vp_mode_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false)
						? { useContentModeFragment: true }
						: {}),
				},
				errorPolicy: 'all',
			},
		).then((result: any) => {
			// Record the end mark and timestamp to track *both* duration of preloading
			// and timestamp of the moment when preloading finished
			window.performance.mark('CFP-63.preloadContent.end');
			window.performance.mark('CFP-63.preloadContentEnd');

			const data = result?.data;
			const homepageId = data?.spaceHomepage?.id;
			if (homepageId) {
				// update result with transformed data
				result.data = transformContentData(data);

				writeQueryInterop<ContentUnifiedQueryType, ContentUnifiedQueryVariablesType>({
					query: ContentUnifiedQueryArtifact,
					variables: {
						...createContentUnifiedQueryVariables({
							contentId: homepageId,
							spaceKey,
							versionOverride,
							isSSR,
							includeRendererAssetsMedia,
							agentSessionsUserId: agentSessionsEnabled ? userId : undefined,
							includeAgentTrustReceiptData: fg('cc_sparks_agent_trust_receipt'),
							includeMetadata:
								!StatsigConfigurations.isKillSwitchOn('confluence_vp_meta_frag_reader') &&
								expVal('confluence_exp_vp_document', 'isEnabled', false),
							includeClassification:
								!StatsigConfigurations.isKillSwitchOn(
									'confluence_vp_classification_context_frag_reader',
								) && expVal('confluence_exp_vp_document', 'isEnabled', false),
							includeReactions:
								!StatsigConfigurations.isKillSwitchOn('confluence_vp_reactions_frag_reader') &&
								expVal('confluence_exp_vp_document', 'isEnabled', false),
							includeAudioContent:
								!StatsigConfigurations.isKillSwitchOn('confluence_vp_audio_frag_reader') &&
								expVal('confluence_exp_vp_document', 'isEnabled', false),
							autohighlightsQueryVariables,
						}),
						...(!StatsigConfigurations.isKillSwitchOn('confluence_vp_mode_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false)
							? { useContentModeFragment: true }
							: {}),
					},
					data: { ...result.data },
				});
			}

			processContentPreloadPermissionError(result, isAccessLinkRoute);

			return { result, hasErrors: result?.hasErrors };
		});
	}

	const chooseContentUnifiedQuery = isBlog
		? ContentBlogUnifiedQueryArtifact
		: ContentUnifiedQueryArtifact;
	const queryVariables: ChooseContentUnifiedQueryVariablesType = isBlog
		? {
				...createContentBlogUnifiedQueryVariables({
					contentId,
					spaceKey,
					versionOverride,
					isSSR,
					includeRendererAssetsMedia,
					preloadLabels: isSSR,
					includeAgentTrustReceiptData: fg('cc_sparks_agent_trust_receipt'),
					includeBylineContributors:
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_title_byline_frag_dedup') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					includeAutomationDiscoveries: fg('cc_automations_discovery_nudges'),
					includeMetadata:
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_meta_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					includeClassification:
						!StatsigConfigurations.isKillSwitchOn(
							'confluence_vp_classification_context_frag_reader',
						) && expVal('confluence_exp_vp_document', 'isEnabled', false),
					includeReactions:
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_reactions_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					includeAudioContent:
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_audio_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
					includeAnalyticsByline:
						hasDocumentContractAnalyticsByline &&
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_analytics_byline_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
					includeContentHeader: shouldUseContentHeaderFragment,
					includeContentHeaderAccess:
						shouldUseContentHeaderFragment &&
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_access_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					includeFabricEditorEligibility: shouldUseFabricEligibilityFragment,
					includeContentHeaderPublicLink:
						shouldUseContentHeaderFragment &&
						!isBlog &&
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_access_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
					includeRendererAssetsDsp,
					rendererAssetsDspAction,
					autohighlightsQueryVariables,
				}),
				...(!StatsigConfigurations.isKillSwitchOn('confluence_vp_mode_frag_reader') &&
				expVal('confluence_exp_vp_document', 'isEnabled', false)
					? { useContentModeFragment: true }
					: {}),
			}
		: {
				...createContentUnifiedQueryVariables({
					contentId,
					spaceKey,
					versionOverride,
					isSSR,
					includeRendererAssetsMedia,
					preloadLabels: isSSR,
					agentSessionsUserId: agentSessionsEnabled ? userId : undefined,
					includeAgentTrustReceiptData: fg('cc_sparks_agent_trust_receipt'),
					includeBylineContributors:
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_title_byline_frag_dedup') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					includeAutomationDiscoveries: fg('cc_automations_discovery_nudges'),
					includeMetadata:
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_meta_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					includeClassification:
						!StatsigConfigurations.isKillSwitchOn(
							'confluence_vp_classification_context_frag_reader',
						) && expVal('confluence_exp_vp_document', 'isEnabled', false),
					includeReactions:
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_reactions_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					includeAudioContent:
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_audio_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
					includeAnalyticsByline:
						hasDocumentContractAnalyticsByline &&
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_analytics_byline_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
					includeContentHeader: shouldUseContentHeaderFragment,
					includeContentHeaderAccess:
						shouldUseContentHeaderFragment &&
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_access_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					includeFabricEditorEligibility: shouldUseFabricEligibilityFragment,
					includeContentHeaderPublicLink:
						shouldUseContentHeaderFragment &&
						!isBlog &&
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_access_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
					includeRendererAssetsDsp,
					rendererAssetsDspAction,
					autohighlightsQueryVariables,
				}),
				...(!StatsigConfigurations.isKillSwitchOn('confluence_vp_mode_frag_reader') &&
				expVal('confluence_exp_vp_document', 'isEnabled', false)
					? { useContentModeFragment: true }
					: {}),
			};

	return query<
		ContentUnifiedQueryType | ContentBlogUnifiedQueryType,
		ChooseContentUnifiedQueryVariablesType
	>({
		query: chooseContentUnifiedQuery,
		variables: queryVariables,
		errorPolicy: 'all',
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr -- This controls request-scoped cache retention, not rendered output.
		...(!process.env.REACT_SSR &&
		expVal('confluence_relay_vp_preload_retention', 'isEnabled', false)
			? {
					relayQueryPreloadRetention: 'environment',
				}
			: {}),
	}).then((result: any) => {
		// Record the end mark and timestamp to track *both* duration of preloading
		// and timestamp of the moment when preloading finished
		window.performance.mark('CFP-63.preloadContent.end');
		window.performance.mark('CFP-63.preloadContentEnd');

		processContentPreloadPermissionError(result, isAccessLinkRoute);

		return { result, hasErrors: result?.hasErrors };
	});
}
